import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Field, TextInput } from "../../components/crm/ui";
import { Clock, Save, AlertTriangle } from "lucide-react";

const LABELS = {
  new_installation: "New Installation",
  repair_maintenance: "Repair & Maintenance",
  amc: "AMC (Annual Maintenance Contract)",
};

export default function SLAMaster() {
  const [slas, setSlas] = useState([]);
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState(null);

  async function load() {
    const r = await api.get("/sla");
    setSlas(r.data);
    setEdited({});
  }
  useEffect(() => { load(); }, []);

  async function saveOne(ticket_type) {
    const hours = Number(edited[ticket_type]);
    if (!hours || hours < 1) { toast.error("SLA hours must be ≥ 1"); return; }
    setSaving(ticket_type);
    try {
      await api.put(`/sla/${ticket_type}?sla_hours=${hours}`);
      toast.success(`${LABELS[ticket_type]} SLA updated to ${hours}h`);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(null); }
  }

  return (
    <div className="space-y-6" data-testid="sla-master-page">
      <PageHeader
        title="SLA Master"
        subtitle="Service-Level Agreement (in hours) per ticket type — applied to every new ticket"
      />

      <div className="bg-white border border-[#DDE5D8] max-w-3xl">
        <div className="px-5 py-4 border-b border-[#DDE5D8] bg-[#0E2818] text-white flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#D4FF7A]" />
          <span className="font-mono text-[10px] uppercase tracking-widest">SLA Formula</span>
          <span className="text-xs ml-2">Deadline = Ticket created_at + SLA hours. After deadline → priority becomes <span className="text-[#D4FF7A] font-semibold">URGENT</span>.</span>
        </div>

        <div className="divide-y divide-[#EEF2EA]">
          {slas.map((s) => {
            const current = edited[s.ticket_type] ?? s.sla_hours;
            const changed = String(current) !== String(s.sla_hours);
            return (
              <div key={s.ticket_type} className="px-5 py-4 grid grid-cols-12 gap-3 items-center" data-testid={`sla-row-${s.ticket_type}`}>
                <div className="col-span-5">
                  <div className="font-display font-semibold text-[#0F1A12]">{LABELS[s.ticket_type] || s.ticket_type}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-0.5">{s.ticket_type}</div>
                </div>
                <div className="col-span-3">
                  <TextInput
                    type="number" min="1" value={current}
                    onChange={(e) => setEdited((p) => ({ ...p, [s.ticket_type]: e.target.value }))}
                    data-testid={`sla-input-${s.ticket_type}`}
                  />
                </div>
                <div className="col-span-2 font-mono text-[11px] text-[#475569]">
                  ≈ {(current / 24).toFixed(1)} days
                </div>
                <div className="col-span-2 text-right">
                  <PrimaryButton onClick={() => saveOne(s.ticket_type)} disabled={!changed || saving === s.ticket_type} data-testid={`sla-save-${s.ticket_type}`}>
                    <Save className="w-3.5 h-3.5" /> {saving === s.ticket_type ? "Saving" : "Save"}
                  </PrimaryButton>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#FFF7E6] border border-[#F5DAB8] p-4 max-w-3xl flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-[#A85800] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#7A4200] leading-relaxed">
          Changes apply to <span className="font-semibold">future tickets only</span>. Tickets created earlier keep their original SLA deadline computed at creation time.
        </div>
      </div>
    </div>
  );
}
